import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

let topicsCache = null;
export function useTopics() {
  const [items, setItems] = useState(topicsCache || []);
  useEffect(() => {
    if (topicsCache) return;
    api.get("/taxonomy/topics").then((r) => { topicsCache = r.data.items; setItems(r.data.items); }).catch(() => {});
  }, []);
  return items;
}

let labelsCache = null;
export function useContentLabels() {
  const [labels, setLabels] = useState(labelsCache || { help: "Help Center", training: "Training" });
  useEffect(() => {
    if (labelsCache) return;
    api.get("/taxonomy/labels").then((r) => { labelsCache = r.data.labels; setLabels(r.data.labels); }).catch(() => {});
  }, []);
  return labels;
}
export const resetTaxonomyCache = () => { topicsCache = null; labelsCache = null; };

// Controlled Shared Topics picker (Super Admin manages the list; editors only choose).
export function TopicPicker({ value = [], onChange, testPrefix = "topic" }) {
  const topics = useTopics();
  const tog = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id].slice(0, 10));
  const chip = (on) => `text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-[#006090] text-white border-[#006090] font-semibold shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-[#006090]/40"}`;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-picker`}>
      {topics.map((t) => <button key={t.id} type="button" title={t.description || ""} className={chip(value.includes(t.id))} onClick={() => tog(t.id)} data-testid={`${testPrefix}-${t.id}`}>{t.name}</button>)}
      {!topics.length && <span className="text-xs text-slate-400">No Shared Topics yet.</span>}
    </div>);
}

export function TopicChips({ ids = [] }) {
  const topics = useTopics();
  const names = topics.filter((t) => ids.includes(t.id));
  if (!names.length) return null;
  return <div className="flex flex-wrap gap-1.5" data-testid="topic-chips">{names.map((t) => <span key={t.id} className="text-xs rounded-full bg-sky-50 text-[#006090] px-2 py-0.5">{t.name}</span>)}</div>;
}

// Simple Topic matching: related Help articles and Training (each filtered by its own permissions server-side).
export function RelatedByTopic({ topicIds = [], exclude, show = ["help", "training"], onOpenHelp }) {
  const [r, setR] = useState(null);
  const key = topicIds.join(",");
  useEffect(() => {
    if (!key) { setR(null); return; }
    api.get("/taxonomy/related", { params: { topics: key, exclude } }).then((x) => setR(x.data)).catch(() => setR(null));
  }, [key, exclude]);
  if (!r) return null;
  const help = show.includes("help") ? r.help : [];
  const tr = show.includes("training") ? r.training : [];
  if (!help.length && !tr.length) return null;
  const box = "block rounded-lg border border-slate-200 p-3 hover:border-[#006090]/40 text-sm font-medium text-slate-800";
  return (
    <div className="mt-8 space-y-6" data-testid="related-by-topic">
      {tr.length > 0 && <section><h2 className="text-base md:text-lg font-semibold text-slate-900 mb-2">Related Training</h2>
        <ul className="grid sm:grid-cols-2 gap-2">{tr.map((t) => <li key={t.id}><Link to={`/training/${t.id}`} className={box} data-testid={`related-training-${t.id}`}>{t.title}</Link></li>)}</ul></section>}
      {help.length > 0 && <section><h2 className="text-base md:text-lg font-semibold text-slate-900 mb-2">Related Help Articles</h2>
        <ul className="grid sm:grid-cols-2 gap-2">{help.map((h) => <li key={h.slug}>{onOpenHelp
          ? <button type="button" onClick={() => onOpenHelp(h.slug)} className={`${box} w-full text-left`} data-testid={`related-help-${h.slug}`}>{h.title}</button>
          : <Link to={`/help/article/${h.slug}`} className={box} data-testid={`related-help-${h.slug}`}>{h.title}</Link>}</li>)}</ul></section>}
    </div>);
}
